import { ArrowRight } from 'lucide-react';
import type { DesignChange } from './design-diff';

function Swatch({ color }: { color?: string }) {
  // No value → the live site's build-time default (publishing reverts to it).
  if (!color) return <span className="text-xs text-gray-400 italic">default</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="h-4 w-4 rounded border border-black/10"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      <span className="text-xs font-mono text-gray-600">{color}</span>
    </span>
  );
}

/**
 * Renders a field-level diff (from diffDesign) of a candidate against the live
 * design — shown in the sandbox's "Review changes" view and inside the Publish /
 * Revert confirmation so the admin sees exactly what goes live.
 */
export default function DesignDiff({ changes }: { changes: DesignChange[] }) {
  if (changes.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        This matches your live design — nothing would change.
      </p>
    );
  }
  return (
    <ul className="space-y-2" aria-label="Changes versus your live design">
      {changes.map((c) => (
        <li key={c.key} className="flex items-center justify-between gap-3 text-sm">
          <span className="text-gray-600 flex-shrink-0">{c.label}</span>
          <span className="flex items-center gap-2 min-w-0">
            {c.kind === 'color' ? (
              <Swatch color={c.before} />
            ) : (
              <span className="text-gray-500 truncate">{c.before ?? 'default'}</span>
            )}
            <ArrowRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" aria-label="changes to" />
            {c.kind === 'color' ? (
              <Swatch color={c.after} />
            ) : (
              <span className="font-medium text-gray-900 truncate">{c.after ?? 'default'}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
